/**
 * Wrecks persist, and salvage is supply (IDEAS X15, at L2). A battle's report books the wrecks it left on the system,
 * both sides', by kit; a held system's wrecks are salvage that adds to its holder's supply where a squad rests
 * (home or a depot), a fraction of the kit's cost, worth less on wrecked ground; the panel says what lies there.
 * Counts now; the wrecks' positions on the ground wait on R13's generator version (lane A). The booking is data
 * (always); the salvage income is behind the sector rule `salvage`. Pure.
 */
import type { UnitKit } from '../types';
import { KIT_PRESETS, type KitPresetKey } from '../core/unitModifiers';
import type { BattleOutcome, BattleSetup, SectorRules, SectorSystem } from './types';

/** Wrecks by kit preset ('custom' for a kit that matches none). */
export type Wrecks = Record<string, number>;

/** A squad at rest salvages this many wrecks a day. */
export const SALVAGE_PER_DAY = 2;
/** What a wreck is worth: this much supply, plus this share of its kit's cost. */
export const SALVAGE_BASE = 2;
export const SALVAGE_SHARE = 0.5;

const same = (a: UnitKit, b: UnitKit): boolean => JSON.stringify(norm(a)) === JSON.stringify(norm(b));
function norm(k: UnitKit): unknown {
  const ent = (o: Record<string, number | undefined>) => Object.entries(o).filter(([, v]) => v !== undefined && v !== 0).sort();
  return [ent(k.launcher as Record<string, number>), ent(k.suit as Record<string, number>), [...k.equipment].sort(), ent(k.traits as unknown as Record<string, number>)];
}

/** The preset a kit is, or 'custom'. */
export function kitPresetKeyOf(kit: UnitKit): KitPresetKey | 'custom' {
  for (const [key, preset] of Object.entries(KIT_PRESETS) as Array<[KitPresetKey, UnitKit]>) if (same(kit, preset)) return key;
  return 'custom';
}

/** The wrecks a battle left: every unit that fell (not extracted), by its side and kit. */
export function wrecksOf(setup: BattleSetup, outcome: BattleOutcome): BattleOutcome['wrecks'] {
  const out: NonNullable<BattleOutcome['wrecks']> = {};
  for (const side of setup.sides) {
    for (const u of side.units) {
      const r = outcome.units.find(x => x.unitId === u.unitId);
      if (!r || r.alive) continue;
      const key = kitPresetKeyOf(u.kit);
      const mine = (out[side.faction] ??= {});
      mine[key] = (mine[key] ?? 0) + 1;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

/** Add a battle's wrecks to what already lies on the system (both sides' — a wreck is nobody's). */
export function addWrecks(prev: Wrecks | undefined, booked: BattleOutcome['wrecks']): Wrecks | undefined {
  if (!booked) return prev;
  const out: Wrecks = { ...(prev ?? {}) };
  for (const byKit of Object.values(booked)) for (const [kit, n] of Object.entries(byKit ?? {})) out[kit] = (out[kit] ?? 0) + n;
  return out;
}

/** How many wrecks lie on a system. */
export function wreckCount(w: Wrecks | undefined): number {
  return Object.values(w ?? {}).reduce((a, n) => a + n, 0);
}

/** What one wreck of a kit is worth on this ground: base plus a share of its cost, scaled by how intact the system is. */
export function wreckValue(kit: string, sys: Pick<SectorSystem, 'scars'>, rules: Pick<SectorRules, 'kitCost'>): number {
  const intact = Math.max(0, 1 - sys.scars.massLostFraction);
  return (SALVAGE_BASE + SALVAGE_SHARE * (rules.kitCost[kit] ?? 0)) * intact;
}

/** The salvage a system's wrecks are worth in all. */
export function salvageWorth(sys: Pick<SectorSystem, 'scars' | 'wrecks'>, rules: Pick<SectorRules, 'kitCost'>): number {
  return Object.entries(sys.wrecks ?? {}).reduce((a, [kit, n]) => a + n * wreckValue(kit, sys, rules), 0);
}

/**
 * One day's salvage at a system: up to `SALVAGE_PER_DAY × days` wrecks, the costliest first, their worth to the
 * holder's supply. Returns the wrecks left and the supply gained.
 */
export function salvage(sys: Pick<SectorSystem, 'scars' | 'wrecks'>, rules: Pick<SectorRules, 'kitCost'>, days: number): { wrecks: Wrecks | undefined; supply: number; taken: number } {
  let budget = SALVAGE_PER_DAY * days;
  const left: Wrecks = { ...(sys.wrecks ?? {}) };
  let supply = 0, taken = 0;
  const kits = Object.keys(left).sort((a, b) => (rules.kitCost[b] ?? 0) - (rules.kitCost[a] ?? 0) || a.localeCompare(b));
  for (const kit of kits) {
    while (budget >= 1 && left[kit] > 0) { left[kit]--; budget--; taken++; supply += wreckValue(kit, sys, rules); }
    if (left[kit] <= 0) delete left[kit];
  }
  return { wrecks: Object.keys(left).length ? left : undefined, supply, taken };
}

/** What lies there, in words. */
export function wrecksSummary(sys: Pick<SectorSystem, 'scars' | 'wrecks'>, rules: Pick<SectorRules, 'kitCost'>): string | null {
  const n = wreckCount(sys.wrecks);
  if (!n) return null;
  const parts = Object.entries(sys.wrecks!).sort((a, b) => b[1] - a[1]).map(([kit, c]) => `${kit} ${c}`).join(', ');
  return `${n} wreck${n === 1 ? '' : 's'} (${parts}); salvage worth ${salvageWorth(sys, rules).toFixed(1)}`;
}
